/* eslint-disable react-hooks/exhaustive-deps */
import { LoaderSpinner } from "components/Loader/LoaderSpinner";
import { getAnalytics } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import { GoogleAuthProvider, getAuth } from "firebase/auth";
import {
  get,
  getDatabase,
  onValue,
  push,
  ref,
  remove,
  set,
  update,
} from "firebase/database";
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  listAll,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { config } from "model/config";
import { createContext, useContext, useEffect, useState } from "react";

const FirebaseContext = createContext();

export const FirebaseProvider = ({ children }) => {
  const firebaseConfig = {
    apiKey: "AIzaSyBRcVHPlxGBr0aL_e0IpaDPc3l2CM0yJrg",
    authDomain: "tracker-960ce.firebaseapp.com",
    databaseURL: "https://tracker-960ce-default-rtdb.firebaseio.com",
    projectId: "tracker-960ce",
    storageBucket: "tracker-960ce.appspot.com",
    messagingSenderId: "385920580993",
    appId: "1:385920580993:web:9a64ad9bd174e556ea7c80",
    measurementId: "G-K2BXJE3FQW"
  };
  const [firebase, setFirebase] = useState(null);

  useEffect(() => {
    const app = initializeApp(firebaseConfig);
    const provider = new GoogleAuthProvider();
    provider.addScope(config.firebaseGoogleProviderScope);
    if (!config.development) {
      provider.setCustomParameters({
        scope: "email",
        hd: config.domain,
      });
    }
    const db = getDatabase(app);
    const storage = getStorage(app);
    setFirebase({
      app,
      auth: getAuth(app),
      googleAuthProvider: provider,
      analytics: getAnalytics(app),
      db: {
        instance: db,
        insert: async (path, data) => {
          console.log("Insert called with path:", path, "and data:", data);
          const dbRef = ref(db, path);
          console.log("dbRef created:", dbRef);
          const itemRef = push(dbRef);
          console.log("itemRef created:", itemRef);
          await set(itemRef, {
            ...data,
            createdAt: new Date().toISOString(),
          });
          console.log("Data set, itemRef.key:", itemRef.key);
          if (!itemRef.key) {
            console.error("Failed to generate a key for the new item");
            throw new Error("Failed to generate a key for the new item");
          }
          return itemRef.key;
        },
        insertWithKey: async (path, key, data) => {
          console.log(
            "Insert with key called with path:",
            path,
            "key:",
            key,
            "and data:",
            data
          );
          const dbRef = ref(db, `${path}/${key}`);
          console.log("dbRef created:", dbRef);
          await set(dbRef, {
            ...data,
            createdAt: new Date().toISOString(),
          });
          console.log("Data set at key:", key);
          return key;
        },
        insertValue: async (path, data) => {
          await set(ref(db, path), data);
        },
        update: async (path, id, data) => {
          await update(ref(db, `${path}/${id}`), {
            ...data,
            updatedAt: new Date().toISOString(),
          });
        },
        listen: (path, onChange) => {
          return onValue(ref(db, path), (snapshot) => {
            const data = snapshot.val();
            onChange(data);
          });
        },
        delete: async (path, id) => {
          await remove(ref(db, `${path}/${id}`));
        },
        push: async (path, data) => {
          await set(ref(db, path), data);
        },
        get: async (path, id) => {
          const result = await get(ref(db, `${path}/${id}`));
          return result;
        },
        getAllCollection: async (path, id = "") => {
          const dbRef = ref(db, id ? `${path}/${id}` : path);
          const snapshot = await get(dbRef);

          if (!snapshot.exists()) {
            return null;
          }

          if (id) {
            return { id, ...snapshot.val() };
          } else {
            // If fetching a collection, map over the data and include each key as 'id'
            const data = snapshot.val();
            return Object.entries(data || {}).map(([key, value]) => ({
              id: key,
              ...value,
            }));
          }
        },
        exists: async (path, id) => {
          if (id === undefined) {
            return false;
          }
          const snapshot = await get(ref(db, `${path}/${id}`));
          return snapshot.exists();
        },
      },
      storage: {
        instance: storage,
        upload: async (file, path = "") => {
          const fileRef = storageRef(
            storage,
            `${path ? `${path}/` : ""}${file.name}`
          );
          const result = await uploadBytes(fileRef, file);
          return result;
        },
        get: async (path, fname = "") => {
          const listRef = storageRef(
            storage,
            `${path}${fname ? `/${fname}` : ""}`
          );
          const result = await listAll(listRef);
          return result;
        },
        getFiles: async (path) => {
          const listRef = storageRef(storage, path);
          const result = await listAll(listRef);
          return result.items;
        },
        getFileUrl: async (path, fname = "") => {
          const fileRef = storageRef(
            storage,
            `${path}${fname ? `/${fname}` : ""}`
          );
          const url = await getDownloadURL(fileRef);
          return url;
        },
        download: async (path, fname = "") => {
          const fileRef = storageRef(
            storage,
            `${path}${fname ? `/${fname}` : ""}`
          );
          const url = await getDownloadURL(fileRef);
          window.open(url, "_blank");
          return url;
        },
        delete: async (path, fname = "") => {
          const fileRef = storageRef(
            storage,
            `${path}${fname ? `/${fname}` : ""}`
          );
          await deleteObject(fileRef);
          return true;
        },
      },
    });
  }, []);

  return (
    <FirebaseContext.Provider value={firebase}>
      {!!firebase ? children : <LoaderSpinner />}
    </FirebaseContext.Provider>
  );
};

export const useFirebase = () => useContext(FirebaseContext);
